/*
 * Global Selected Vehicle Bar
 *
 * This script runs on every page to display a small bar showing the
 * vehicle that the customer previously selected via the year/make/model
 * picker. It reads from localStorage and inserts a bar after the
 * header. A reset button allows clearing the selection without
 * navigating back to the home page. Styling is handled via the
 * `.global-selected-vehicle` CSS class defined in custom.css.
 */

document.addEventListener('DOMContentLoaded', function () {
  let stored;
  try {
    stored = JSON.parse(localStorage.getItem('selectedVehicle'));
  } catch (e) {
    stored = null;
  }
  if (!stored || !stored.year || !stored.make || !stored.model) {
    return;
  }
  /**
   * If we're on the search results page and a `cat` query parameter is
   * provided, filter the visible product cards so that only wheels or
   * packages are shown. This implementation waits for the product grid
   * to load and listens for subsequent updates (e.g. after sorting or
   * filtering) via a MutationObserver. When filtering for packages we
   * recognise a wider range of keywords (e.g. "package", "packages",
   * "pkg", "kit", "mounted", "mount", "balance", "balanced", "tire",
   * "tires") to identify package products more robustly. If no
   * products match the requested category once items are present in the
   * grid, a styled message is displayed. The message is inserted
   * directly into the grid so that it appears in the same context as
   * the product cards.
   */
  function maybeFilterSearchResults() {
    // Only run on search results pages. The vehicle SEO URL system can display
    // a pretty vehicle URL while the rendered page is still powered by /search.
    if (!/\/search/.test(window.location.pathname) && window.duallySearchBasePath !== '/search') return;
    const params = new URLSearchParams(window.location.search);
    let cat = params.get('cat');
    if (!cat) return;
    cat = cat.toLowerCase();

    // Helper to determine if a given product card appears to be a package
    function isPackageProduct(text) {
      // Normalize the text to lower case once
      const t = text.toLowerCase();
      // Check for common keywords that indicate a package or kit. Include
      // variations like "pkg", "kit", "bundle", "combo", "set", "mounted",
      // "balanced", and ensure we treat products mentioning both wheel and tire
      // together as packages. Add additional synonyms like "assembly" and
      // "complete" to catch more cases.
      if (t.includes('tire') && t.includes('wheel')) return true;
      const keywords = [
        'package',
        'packages',
        'pkg',
        'kit',
        'kits',
        'bundle',
        'combo',
        'set',
        'mounted',
        'mount',
        'balanced',
        'balance',
        'assembly',
        'complete',
        'combo',
        'mounted and balanced',
        'mount & balance',
        'mounted & balanced',
        'wheel+tire',
        'tire+wheel'
      ];
      return keywords.some((kw) => t.includes(kw));
    }

    // Apply filtering logic to the current grid
    function applyFilterToGrid(gridEl) {
      if (!gridEl) return;
      const items = gridEl.querySelectorAll('.grid__item');
      if (!items.length) return;
      let visible = 0;
      items.forEach((item) => {
        // Reset the display; this ensures we can re-apply filters on updated lists
        item.style.display = '';
        const text = (item.textContent || '').trim();
        const isPkg = isPackageProduct(text);
        if (cat === 'wheels') {
          // Hide package-related products when viewing wheels
          if (isPkg) {
            item.style.display = 'none';
          } else {
            visible++;
          }
        } else if (cat === 'packages') {
          // Hide non-package products when viewing packages
          if (!isPkg) {
            item.style.display = 'none';
          } else {
            visible++;
          }
        }
      });
      // Insert or remove the "no products" message based on the visibility
      const existingMsg = gridEl.querySelector('.no-products-message');
      if (existingMsg) existingMsg.remove();
      if (items.length > 0 && visible === 0) {
          const msg = document.createElement('p');
          msg.className = 'no-products-message';
          msg.style.color = '#ff0000';
          msg.style.fontWeight = '900';
          msg.style.fontSize = '1.5rem';
          msg.style.marginTop = '3rem';
          msg.style.textAlign = 'center';
          msg.textContent = 'We Are Sorry There Are No Products Matching Your Vehicle Selected';
          gridEl.appendChild(msg);
      }
    }

    // Set up observation for changes in the product grid. The search page uses
    // dynamic loading, replacing the #product-grid element entirely when
    // sorting or filtering. We observe the container and reapply the filter
    // whenever a new product grid is injected. Additionally, we observe the
    // grid itself for child mutations.
    function initObservers() {
      const container = document.getElementById('ProductGridContainer');
      if (!container) {
        // Fallback: apply filter to any existing grid once
        const fallbackGrid = document.getElementById('product-grid');
        if (fallbackGrid) {
          applyFilterToGrid(fallbackGrid);
        }
        return;
      }
      let gridObserver = null;
      // Helper to attach an observer to the current grid
      function attachGridObserver() {
        const gridEl = document.getElementById('product-grid');
        if (!gridEl) return;
        // Apply filter immediately to the current grid
        applyFilterToGrid(gridEl);
        // Disconnect any previous grid observer
        if (gridObserver) {
          gridObserver.disconnect();
        }
        // Observe for changes within the grid (e.g., product list updates)
        try {
          gridObserver = new MutationObserver(() => {
            applyFilterToGrid(gridEl);
          });
          gridObserver.observe(gridEl, { childList: true, subtree: true });
        } catch (err) {
          // In case MutationObserver fails, fall back to periodic polling
          const intervalId = setInterval(() => {
            applyFilterToGrid(gridEl);
          }, 500);
          // Stop polling after 10 seconds
          setTimeout(() => clearInterval(intervalId), 10000);
        }
      }
      // Attach initial grid observer if grid exists now
      attachGridObserver();
      // Observe the container for new grid replacements
      try {
        const containerObserver = new MutationObserver(() => {
          // When the container changes, reattach the grid observer
          attachGridObserver();
        });
        containerObserver.observe(container, { childList: true });
      } catch (err) {
        // Fallback: check periodically if the grid has been replaced
        const checkIntervalId = setInterval(() => {
          attachGridObserver();
        }, 500);
        setTimeout(() => clearInterval(checkIntervalId), 10000);
      }
    }
    initObservers();
  }

  /**
   * Automatically applies the bolt pattern filter on collection pages.
   *
   * When a vehicle with a bolt pattern is selected and the customer
   * navigates to the Wheels or Packages collection, this helper will
   * attempt to select the corresponding bolt pattern facet. It searches
   * the filter checkboxes within the facet form and clicks the one
   * matching the stored bolt pattern (case‑insensitive). If the facets
   * have not yet been rendered, it retries for a short period until
   * either the checkbox is found and clicked or the retry window
   * expires. This ensures the collection is filtered to only show
   * products compatible with the selected bolt pattern without
   * requiring a page reload or search query.
   */
  function applyBoltFilterToCollection() {
    if (!stored || !stored.bolt) return;
    const path = window.location.pathname;
    // Only apply on the wheels or packages collections
    if (!/\/collections\/(wheels|packages)/.test(path)) return;
    const bolt = String(stored.bolt).toLowerCase();
    function attempt() {
      const form = document.getElementById('FacetFiltersForm');
      if (!form) return false;
      const inputs = form.querySelectorAll('input[type="checkbox"]');
      if (!inputs.length) return false;
      let clicked = false;
      inputs.forEach((input) => {
        if (!clicked) {
          const val = String(input.value || '').toLowerCase();
          if (val === bolt && !input.checked && !input.disabled) {
            // Simulate a user click to trigger Shopify's filtering logic
            input.click();
            clicked = true;
          }
        }
      });
      return clicked;
    }
    // Try immediately; if not found, poll for a short time
    if (!attempt()) {
      const intervalId = setInterval(() => {
        if (attempt()) {
          clearInterval(intervalId);
        }
      }, 300);
      setTimeout(() => clearInterval(intervalId), 5000);
    }
  }

  // Invoke the filtering logic immediately (no initial delay). The
  // filtering function itself handles waiting for the grid to be ready
  // and responding to dynamic updates via a MutationObserver.
  maybeFilterSearchResults();
  // Apply bolt pattern facet filtering on collection pages. This will
  // automatically select the bolt pattern filter when on wheels or
  // packages collections to show only compatible products.
  applyBoltFilterToCollection();
  // If a bolt pattern is stored, use it to filter the wheels and packages
  // collections. When the customer navigates directly to a wheels or
  // packages collection (without a query parameter), redirect them to
  // a search page filtered by the bolt pattern. This ensures that
  // customers see only products compatible with their selected vehicle.
  if (stored && stored.bolt) {
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const boltQuery = encodeURIComponent(stored.bolt);
    const hasBoltQuery = currentSearch && currentSearch.includes('q=' + boltQuery);


    /*
      Do not automatically redirect to the search page or append query
      parameters on collection pages. Instead, when a customer visits the
      Wheels or Packages collection and has a selected bolt pattern, we
      apply the appropriate bolt filter on the existing collection page
      using the native Shopify facets. Keeping the user on the
      collection page preserves category‑specific filters and avoids
      showing wheel filters when viewing packages.
    */
    // No redirect is performed here. Filtering by bolt will be handled by
    // the applyBoltFilterToCollection() function defined later.

    // On the home page (or any page containing category tiles), update the
    // Wheels and Packages tile links to point to the filtered search. We
    // include a `cat` parameter on the URL so that the search page can
    // determine which category was clicked and filter results accordingly.
    try {
      const tileLinks = document.querySelectorAll('.dually-tiles-grid .dually-tile');
      tileLinks.forEach((tile) => {
        const href = tile.getAttribute('href');
        if (!href) return;
        // Update the Wheels and Packages tiles to link to the respective
        // collections. We avoid appending a query parameter here and
        // instead rely on the applyBoltFilterToCollection function to
        // automatically select the bolt pattern filter once the page has
        // loaded. This preserves the correct facets for the collection.
        if (/\/collections\/wheels/.test(href)) {
          tile.setAttribute('href', '/collections/wheels');
        } else if (/\/collections\/packages/.test(href)) {
          tile.setAttribute('href', '/collections/packages');
        }
      });
    } catch (err) {
      console.warn('Failed to update category tile links for bolt filtering', err);
    }
  }
  // The selected vehicle is now shown inside the Year / Make / Model selector only.
  // Keep the filtering/link behavior above, but do not inject the old header vehicle bar.
  return;

  // Build the bar elements
  const bar = document.createElement('div');
  // Apply both our custom bar styling and Shopify's page-width to match the logo alignment
  bar.className = 'global-selected-vehicle page-width';
  const text = document.createElement('span');
  text.textContent = `Your Vehicle: ${stored.year} ${stored.make} ${stored.model}`;
  const resetBtn = document.createElement('button');
  resetBtn.textContent = 'Reset';
  resetBtn.className = 'reset-button';
  // Attach reset behaviour
  resetBtn.addEventListener('click', function () {
    try {
      localStorage.removeItem('selectedVehicle');
    } catch (err) {
      console.error('Failed to clear selected vehicle', err);
    }
    // Remove bar from the DOM
    bar.remove();
    // After clearing the vehicle selection, navigate to show all products in the current context
    const path = window.location.pathname;
    // If on a collection page with optional query params, strip the query to show all products
    if (path.includes('/collections/')) {
      const base = path.split('?')[0];
      window.location.href = base;
    } else if (path.includes('/search')) {
      // On search pages, attempt to redirect back to the relevant collection if known.
      try {
        const params = new URLSearchParams(window.location.search);
        const cat = params.get('cat');
        if (cat === 'wheels' || cat === 'packages') {
          // Redirect back to the Wheels or Packages collection to restore the
          // grid view with secondary images enabled.
          window.location.href = '/collections/' + cat;
        } else {
          // If no specific category is provided, fall back to the all products collection.
          window.location.href = '/collections/all';
        }
      } catch (err) {
        // Fallback for any parsing error: show all products
        window.location.href = '/collections/all';
      }
    } else {
      // Fallback: reload the home page
      window.location.href = '/';
    }
  });
  bar.appendChild(text);
  bar.appendChild(resetBtn);
  // Insert the bar after the site header if present
  const headerWrapper = document.querySelector('.header-wrapper');
  if (headerWrapper && headerWrapper.parentNode) {
    headerWrapper.parentNode.insertBefore(bar, headerWrapper.nextSibling);
    /*
      Ensure the page is scrolled to the top after inserting the global
      selected vehicle bar. Without this, certain pages (like the search
      results landing page) may auto‑scroll down due to other scripts
      focusing inputs or restoring scroll position, causing the logo to
      appear off screen with only the vehicle bar visible (see the user’s
      screenshot). Resetting the scroll position here guarantees the
      header and logo remain visible immediately beneath the browser
      chrome when navigating to Wheels or Packages after selecting a
      vehicle.
    */
    try {
      window.scrollTo({ top: 0, left: 0 });
    } catch (err) {
      // Fallback for older browsers
      window.scrollTo(0, 0);
    }
    // Some pages (e.g. search results) may trigger additional scrolling
    // shortly after DOMContentLoaded (for example when focusing a search
    // input). Schedule a second scroll reset on the next tick to ensure
    // the header remains visible after those scripts run.
    setTimeout(function () {
      try {
        window.scrollTo({ top: 0, left: 0 });
      } catch (err) {
        window.scrollTo(0, 0);
      }
    }, 100);
  } else {
    // If no header found, append to the top of the body
    document.body.insertBefore(bar, document.body.firstChild);
  }
});